




























import Vue from 'vue'
import { mapState, mapGetters, mapActions } from 'vuex'
import SurfaceHeader from 'vuecomponents/surface_header.vue'
import SurfacePosts from 'vuecomponents/surface_posts.vue'
import DoubleClickToPostMixin from 'vuemixins/double_click_to_post_mixin'
import DropToPostMixin from 'vuemixins/drop_to_post_mixin'
import loadMapApi from 'bits/map_api_loader'
import { $ } from 'bits/jquery'
import { listenScrollHeader, unlistenScrollHeader } from 'bits/header_scrolling'
import { subscribeUpdateBodySizeCss, unsubscribeUpdateBodySizeCss } from 'bits/body_sizing'
import window from 'bits/global'
import { RootState } from 'vuexstore/surface/types'
import {
  extractPostAttributesArrayFromClipboardData,
  extractPostAttributesFromClipboardData,
  PasteType,
} from 'bits/clipboard'
import { trackEvent } from 'bits/analytics'
import { isActiveElementAnInputField, isEventOnAnInputField } from 'bits/dom'
import tinykeys from 'tinykeys'

export default Vue.extend({
  components: {
    SurfaceHeader,
    SurfacePosts,
    SurfaceContainerGrid: () => import('vuecomponents/surface_container_grid.vue'),
    SurfaceContainerShelf: () => import('vuecomponents/surface_container_shelf.vue'),
    SurfaceContainerMap: () => import('vuecomponents/surface_container_map.vue'),
    SurfaceContainerTable: () => import('vuecomponents/surface_container_table.vue'),
    SurfaceContainerTimeline: () => import('vuecomponents/surface_container_timeline.vue'),
  },
  mixins: [DoubleClickToPostMixin, DropToPostMixin],
  data() {
    return {
      isMapApiLoaded: false,
      removeKeyCListener: (): void => {
        return
      },
    }
  },
  computed: {
    ...mapGetters('post', ['postBeingEditedCid']),
    ...mapGetters('snackbars', ['xSnackbar']),
    ...mapGetters([
      'xHeader',
      'isPostable',
      'isReadOnly',
      'isApp',
      'format',
      'isMap',
      'isGrid',
      'isShelf',
      'isCanvas',
      'isTable',
      'isTimeline',
      'isStream',
      'isSubscriptionPaused',
      'amIOwner',
      'isUsingNewComposerModal',
      'backgroundLuminance',
      'pressCToPost',
    ]),
    ...mapState(['isOnline', 'isRefreshPending', 'isMobile']),
    ...mapState({
      googleApiKeyForMap: (state: RootState) => state.constants.googleApiKeyForMap,
      currentCountryCode: (state: RootState) => state.constants.currentCountryCode,
    }),
  },
  watch: {
    isOnline(isOnline): void {
      if (isOnline && this.isRefreshPending) this.refresh({ isAutoRefresh: true })
    },
    isPostable(newValue) {
      if (newValue) {
        this.enableNewPostHandlers()
      } else {
        this.disableNewPostHandlers()
      }
    },
    format(newValue, oldValue) {
      if (newValue !== oldValue) {
        // Allow native bridge messages to flush before reloading
        this.$nextTick(() => window.location.reload())
      }
    },
  },
  async mounted() {
    this.requestRefresh({ isAutoRefresh: false })
    // Bring new post into view
    this.autoRefreshOnVisibilityChange()
    if (this.isMap) {
      await loadMapApi(this.googleApiKeyForMap, { region: this.currentCountryCode })
      this.isMapApiLoaded = true
    } else {
      this.$nextTick(() => {
        if (this.isPostable) {
          this.enableNewPostHandlers()
        }
      })
    }
    if (!this.xHeader) {
      // mobile app with native header
      this.resizeHeader({ height: 0 })
    }
    if (this.isCanvas && this.xHeader) {
      listenScrollHeader(this.$refs.wallContainer as HTMLElement, (this.$refs.surfaceHeader as Vue).$el as HTMLElement)
    }
    // Ensure that export as image captures all posts
    if (this.isReadOnly && (this.isGrid || this.isCanvas)) {
      const hookToAllStoreChanges = ((store) => store.subscribe.bind(store))(this.$store)
      const events = ['load', 'beethoven-load', 'error', 'beethoven-error', hookToAllStoreChanges]
      const extraMarginInPx = 200
      subscribeUpdateBodySizeCss('wish', events, extraMarginInPx)
    }
    if (this.isSubscriptionPaused && this.amIOwner) {
      const snackbarData: { message?; persist?; showCloseButton?; actionText?; actionUrl? } = {
        message: this.__('Your subscription is paused. Your padlets are read-only until it resumes.'),
        persist: true,
        showCloseButton: true,
      }
      if (!this.isApp) {
        snackbarData.actionText = this.__('Go to Settings')
        snackbarData.actionUrl = '/dashboard/settings/billing'
      }
      this.setSnackbar(snackbarData)
    }

    if (this.pressCToPost) {
      this.removeKeyCListener = tinykeys(window, {
        KeyC: () => {
          this.handleOnKeydownC()
        },
      })
    }
  },
  beforeDestroy() {
    this.disableNewPostHandlers()
    unlistenScrollHeader()
    unsubscribeUpdateBodySizeCss()
    this.removeKeyCListener()
  },
  methods: {
    ...mapActions(['requestRefresh', 'refresh', 'resizeHeader']),
    ...mapActions('snackbars', ['setSnackbar']),
    ...mapActions('post', ['createPost', 'startNewPost']),
    ...mapActions('map', ['startPickingLocation']),
    ...mapActions('draft', ['startNewDraft']),
    ...mapActions('composerModal', { dockComposerModal: 'dock' }),
    enableNewPostHandlers() {
      if (this.isShelf || this.isGrid || this.isTable || this.isMap || this.isTimeline) return
      if (!this.isMobile) {
        this.enableDoubleClickToPost(this.$el, this.$refs.wishList)
      }
      this.enableDropToPost(this.$el)
      window.addEventListener('paste', this.handleOnPaste as (e: Event) => void)
    },
    disableNewPostHandlers() {
      this.disableDoubleClickToPost(this.$el)
      this.disableDropToPost(this.$el)
      window.removeEventListener('paste', this.handleOnPaste as (e: Event) => void)
    },
    autoRefreshOnVisibilityChange() {
      if (this.autoRefreshEnabled) return
      $(document).on('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.requestRefresh({ isAutoRefresh: true })
        }
      })
      this.autoRefreshEnabled = true
    },
    handleOnPaste(event: ClipboardEvent): void {
      event.stopPropagation()
      if (!this.isPostable) return

      if (isEventOnAnInputField(event)) {
        return
      }

      const data = extractPostAttributesArrayFromClipboardData(event.clipboardData)
      if (!data || !data.length) return

      // Pasting texts from some applications like Microsoft Word may also paste an image version of the text
      // we want to treat them as text
      const isRichTextPastedAsFile =
        event.clipboardData?.types?.includes('text/rtf') && event.clipboardData?.types?.includes('Files')

      // Create a post with attachment without filename
      // when PasteType is File
      const attachment = extractPostAttributesFromClipboardData(data, PasteType.File)

      if (attachment && !isRichTextPastedAsFile) {
        if (this.isUsingNewComposerModal) {
          this.startNewDraft({ attributes: attachment.postAttributes, shouldStartEditing: true })
          this.dockComposerModal()
        } else {
          this.createPost({ attributes: attachment.postAttributes })
        }
        trackEvent('Posts', 'add', `Paste ${attachment.pasteType}`)
        return
      }

      // Create a new post when PasteType is Link or Text or PostContentHash
      const dataWithoutFile = data.filter((datum) => {
        return datum?.pasteType !== PasteType.File
      })
      if (this.isUsingNewComposerModal) {
        this.startNewDraft({ attributes: dataWithoutFile[0].postAttributes, shouldStartEditing: true })
        this.dockComposerModal()
      } else {
        this.createPost({ attributes: dataWithoutFile[0].postAttributes })
      }
      trackEvent('Posts', 'add', `Paste ${dataWithoutFile[0].pasteType}`)
    },
    handleOnKeydownC(): void {
      if (isActiveElementAnInputField()) return
      if (this.isMap) {
        this.startPickingLocation()
      } else {
        this.startNewPost()
      }
    },
  },
})
